import { getEffectiveStepTooltip, namesNoTooltip } from '@sb/motion-planning';
import { calculateSpeedProfileFromStepMotionSpeed } from '@sb/remote-control/util/calculateSpeedProfileFromStepMotionSpeed';
import { ABSOLUTE_MAX_SPEED_PROFILE } from '@sb/routine-runner';
import type { StepKind } from '@sb/routine-runner';

import { getPermittedChildStepKindsShared } from '../../step-rules/moveArmToV2';
import { type ValidationRule, type ValidationContext } from '../../types';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import { isTooltipSupplied } from '../../util/tooltipReference';
import { isTooltipDangling, validateMotionSpeed } from '../../util/validators';

import {
  CanonicalStepRuleBase,
  stepKindDisplayName,
  type CanonicalValidateArgs,
} from './base';

class MoveArmToV2Rule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.MoveArmToV2', 'MoveArmToV2');
  }

  // The warning check returns its finding to `warn()` instead of throwing, so
  // it cannot stand in for an error another check would have raised.
  protected validate({
    children,
    args,
    ctx,
    warn,
  }: CanonicalValidateArgs): void {
    const permittedSteps = this.getPermittedChildSteps(args, ctx);
    this.checkChildrenKinds(children, permittedSteps);
    this.checkConstantSpeed(args, ctx);
    this.checkStepTooltipSupplied(args, ctx);
    this.checkMotionSpeed(args);
    warn(this.checkLegacyConstantSpeedTooltipSupplied(args, ctx));
  }

  /**
   * The step's own tooltip must still be supplied by this robot's equipment.
   *
   * Blocking, unlike the same check on a motion child: `stepTooltip` arrived
   * with the Active Tooltip work, so every step carrying one was authored
   * against a robot that had the equipment. A dangling one is a change made
   * since, not a routine from before the field existed.
   *
   * @throws {ActionRequiredError}
   */
  private checkStepTooltipSupplied(
    args: CanonicalValidateArgs['args'],
    ctx: ValidationContext,
  ): void {
    if (args.argumentKind !== 'MoveArmToV2') return;
    if (!ctx?.featureFlags?.setActiveTooltip) return;

    const { stepTooltip } = args;

    if (stepTooltip?.kind !== 'explicit') return;

    if (
      isTooltipSupplied({
        tcpOption: stepTooltip.tcpOption,
        gripperID: stepTooltip.gripperID,
        equipment: ctx.equipment ?? [],
      })
    ) {
      return;
    }

    throw new ActionRequiredError({
      kind: 'missingEquipment',
      // Never name the raw equipment id: it is long, meaningless to the
      // operator, and overflows the validation popover.
      message: "This tooltip isn't on this robot any more. Select a tooltip.",
      fieldId: 'stepTooltip',
    });
  }

  /**
   * Surface legacy `customLimits` percent-based fields as a deprecation
   * prompt.
   *
   * @throws {Error}
   */
  private checkMotionSpeed(args: CanonicalValidateArgs['args']): void {
    if (args.argumentKind !== 'MoveArmToV2') return;
    validateMotionSpeed('Move Arm', args.motionSpeed);
  }

  /**
   * Constant-speed moves require an explicitly-selected tooltip and blending
   * between segments, and cannot exceed the step's default tooltip speed limit.
   *
   * @throws {ActionRequiredError}
   */
  private checkConstantSpeed(
    args: CanonicalValidateArgs['args'],
    ctx: ValidationContext,
  ): void {
    if (args.argumentKind !== 'MoveArmToV2') return;
    if (!args.enforceConstantSpeed) return;

    // A constant-speed move takes its tooltip from the Move step's own
    // `stepTooltip`; `constantSpeedTCPOption` is the field the removed
    // constant-speed picker wrote, and still governs the steps that predate it.
    //
    // Flag-gated because the flag decides which authoring surface exists: with
    // it on, the Move step's Tooltip row is always populated (an absent
    // `stepTooltip` reads there as the Active Tooltip, which is what the runner
    // resolves too), so there is nothing to select. With it off there is no
    // such row and the legacy field is still required.
    if (namesNoTooltip(args) && !ctx.featureFlags?.setActiveTooltip) {
      throw new ActionRequiredError({
        kind: 'missingConfiguration',
        fieldId: 'constantSpeed',
        message: 'Select a tooltip for the constant speed motion.',
      });
    }

    // Constant speed can only hold a steady tooltip speed through corners if the
    // move blends between segments, so blending cannot be disabled/None while
    // it is enabled. Use 'invalidConfiguration' (blocking error) so
    // precheckForExecution / isRoutinePlayable actually block playback.
    if (!args.blendConfig?.radius) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        fieldId: 'blending',
        message:
          'Constant speed requires blending. Set a blend radius greater than 0.',
      });
    }

    if (!args.constantSpeedValue) return;

    // The step's effective tooltip speed limit for whichever speed-limit option
    // is selected — a custom value, a percent of robot max, or robot max (no
    // limit). Mirrors the step config's toRoutineRunner (and its edit-time
    // getConstantSpeedActionRequired), which derive the speed profile from
    // motionSpeed over ABSOLUTE_MAX_SPEED_PROFILE, so playback is gated for the
    // percent option (e.g. 1% of max), not just the fully-custom one —
    // otherwise the step-settings UI would show an error the play-time gate
    // (precheckForExecution / isRoutinePlayable) doesn't enforce.
    const { maxTooltipSpeed: tcpLimit } =
      calculateSpeedProfileFromStepMotionSpeed({
        motionSpeed: args.motionSpeed,
        baseSpeedProfile: ABSOLUTE_MAX_SPEED_PROFILE,
      });

    if (tcpLimit == null) return;
    if (args.constantSpeedValue <= tcpLimit) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      fieldId: 'constantSpeed',
      message:
        'Constant speed exceeds the default tooltip speed limit. Reduce the constant speed or increase the tooltip speed limit.',
    });
  }

  /**
   * A step with no `stepTooltip` can still name a tooltip outright: the
   * effective tooltip of a constant-speed step that predates the Active
   * Tooltip work resolves through the legacy `constantSpeedTCPOption` field.
   * That named tooltip must still be supplied by this robot, exactly as the
   * legacy `tcpOption` on a waypoint or arc must.
   *
   * Warns rather than blocks, for the reason the motion children's check
   * does: the field predates this work, so a routine authored before it can
   * already carry a dangling one, and already ran that way.
   *
   * @returns a non-blocking warning, or `undefined`.
   */
  private checkLegacyConstantSpeedTooltipSupplied(
    args: CanonicalValidateArgs['args'],
    ctx: ValidationContext,
  ): WarningFinding | undefined {
    if (args.argumentKind !== 'MoveArmToV2') return undefined;

    // A step carrying its own tooltip is checkStepTooltipSupplied's to judge,
    // at the blocking tier its newer field warrants.
    if (args.stepTooltip != null) return undefined;

    const effective = getEffectiveStepTooltip(args);

    // Explicit here can only have come from `constantSpeedTCPOption`; anything
    // else resolves to the Active Tooltip and names nothing to check.
    if (effective.kind !== 'explicit') return undefined;

    if (
      !isTooltipDangling({
        tcpOption: effective.tcpOption,
        gripperID: effective.gripperID,
        ctx,
      })
    ) {
      return undefined;
    }

    return {
      kind: 'danglingTooltip',
      fieldId: 'constantSpeed',
      message:
        "This move's constant-speed tooltip isn't on this robot any more. Speed will be measured at the wrist flange unless you choose another.",
    };
  }

  /**
   * Get the permitted child steps.
   *
   * The step's own arguments are part of the answer: a constant-speed move
   * accepts Arc children whether or not the `arcStepInMove` flag is on.
   *
   * @param args - The step's arguments.
   * @param ctx - The context.
   * @returns The permitted child steps.
   */
  private getPermittedChildSteps(
    args: CanonicalValidateArgs['args'],
    ctx: ValidationContext,
  ): readonly StepKind[] {
    return getPermittedChildStepKindsShared(
      ctx.featureFlags ?? {},
      args.argumentKind === 'MoveArmToV2' ? args : undefined,
    );
  }

  /**
   * Check if the children kinds are valid.
   * @param children - The children.
   * @param permittedSteps - The permitted steps.
   * @throws {ActionRequiredError}
   */
  private checkChildrenKinds(
    children: Array<{ stepKind: StepKind | string; name?: string }>,
    permittedSteps: readonly StepKind[],
  ): void {
    const allOk = children.every((c) =>
      permittedSteps.includes(c.stepKind as StepKind),
    );

    if (allOk) return;

    const badStep = children.find(
      (c) => !permittedSteps.includes(c.stepKind as StepKind),
    );

    if (!badStep) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Move Arm steps must contain only valid child steps.',
      });
    }

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `Move Arm steps may not contain ${stepKindDisplayName(
        badStep.stepKind,
      )} steps.`,
    });
  }
}

export const MoveArmToV2Validation: ValidationRule =
  new MoveArmToV2Rule().toValidationRule();
